function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

function jsonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-CSRF-Token": csrfToken() ?? "",
  }
}

// Partial update of the workspace record — the app saves one field at a time.
export async function saveWorkspace(url: string, fields: { name?: string; description?: string; objective?: string }): Promise<boolean> {
  if (!csrfToken()) return false

  try {
    const response = await fetch(url, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ workspace: fields }),
    })
    return response.ok
  } catch {
    return false
  }
}

// objective.md as the server has it. `generating` is true while the workspace
// has a description but no objective yet — the window in which the generation
// job is still working.
export type ObjectiveState = {
  objective: string
  generating: boolean
}

export async function fetchObjective(url: string): Promise<ObjectiveState | null> {
  try {
    const response = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!response.ok) return null
    return (await response.json()) as ObjectiveState
  } catch {
    return null
  }
}

// One directory of the sandbox's workspace — schemas/v1/file_listing.json.
// Every field past path/name/kind is optional there, so every one is optional
// here: an older runtime answers with less, not with an error.
export type FileEntry = {
  path: string
  name: string
  kind: "file" | "dir"
  modified_at?: string
  size_bytes?: number
  entry_count?: number
  title?: string | null
  front_matter?: Record<string, string> | null
  front_matter_error?: string
  // The opening of the document, title line dropped and whitespace collapsed.
  // Absent for anything the runtime could not read as text.
  preview?: string | null
  preview_truncated?: boolean
}

export type FileListing = {
  path: string
  entries: FileEntry[]
  truncated?: boolean
  generated_at?: string
}

// null when the sandbox could not be reached — which the caller must show as a
// problem, not as an empty folder.
//
// `exclude` is names the caller does not want back, literal or glob, matched by
// the runtime against an entry's own name. It goes as exclude[] so Rails reads
// it as a list rather than keeping only the last one.
export async function fetchFiles(
  filesUrl: string,
  path: string,
  exclude: string[] = [],
): Promise<FileListing | null> {
  try {
    const query = new URLSearchParams([
      ["path", path],
      ...exclude.map((name): [string, string] => ["exclude[]", name]),
    ])
    const response = await fetch(`${filesUrl}?${query.toString()}`, {
      headers: { "Accept": "application/json" },
    })
    if (!response.ok) return null
    return (await response.json()) as FileListing
  } catch {
    return null
  }
}

// One file's text. null when the sandbox could not be reached, which a caller
// must show as a problem rather than as an empty document.
export async function fetchFileText(fileUrl: string, path: string): Promise<string | null> {
  try {
    const response = await fetch(`${fileUrl}?path=${encodeURIComponent(path)}`, {
      headers: { "Accept": "text/plain" },
    })
    if (!response.ok) return null
    return await response.text()
  } catch {
    return null
  }
}

// Writes a file back into the sandbox. Returns whether it landed — the caller
// keeps the draft on screen either way, so a failure costs nothing typed.
export async function saveFileText(fileUrl: string, path: string, content: string): Promise<boolean> {
  if (!csrfToken()) return false

  try {
    const response = await fetch(fileUrl, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ path, content }),
    })
    return response.ok
  } catch {
    return false
  }
}

// Renames a file in place, keeping its extension (the server enforces that).
// Returns null when it landed, or the reason to show beside the field.
export async function renameFile(
  renameUrl: string,
  path: string,
  name: string,
): Promise<{ path: string } | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(renameUrl, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ path, name }),
    })
    const body = (await response.json()) as { path?: string; error?: string }
    if (!response.ok || !body.path) return body.error ?? "Could not rename it."
    return { path: body.path }
  } catch {
    return "Could not reach the server."
  }
}

// What the runtime last said an agent was doing. Null throughout for an agent
// that has never reported. `status_live` is the server's answer to whether it
// is worth showing: the agent is working and there is still a runtime saying
// so — see AgentsController#status_json.
export type AgentPresence = {
  status: string | null
  status_detail: string | null
  status_at: string | null
  status_live: boolean
}

export type AgentRecord = AgentPresence & {
  id: string
  name: string | null
  model: string
  model_label: string
}

export async function fetchAgents(agentsUrl: string): Promise<AgentRecord[] | null> {
  try {
    const response = await fetch(agentsUrl, { headers: { "Accept": "application/json" } })
    if (!response.ok) return null
    const body = (await response.json()) as { agents: AgentRecord[] }
    return body.agents
  } catch {
    return null
  }
}

export async function createAgent(
  agentsUrl: string,
  fields: { name?: string; model?: string } = {},
): Promise<AgentRecord | null> {
  if (!csrfToken()) return null

  try {
    const response = await fetch(agentsUrl, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ agent: fields }),
    })
    if (!response.ok) return null
    const body = (await response.json()) as { agent: AgentRecord }
    return body.agent
  } catch {
    return null
  }
}

// The one-time launch: creates the initial agents server-side and stamps the
// workspace as quickstarted. Returns the created agents, or null on failure.
export async function quickstartWorkspace(url: string, agentNames: string[]): Promise<AgentRecord[] | null> {
  if (!csrfToken()) return null

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ agent_names: agentNames }),
    })
    if (!response.ok) return null
    const body = (await response.json()) as { agents: AgentRecord[] }
    return body.agents
  } catch {
    return null
  }
}

export type AgentMessageRecord = {
  id: number
  role: "user" | "agent" | "system"
  kind: string
  contents: string | null
  summary: string | null
  round_number: number | null
  created_at: string
}

// The transcript poll carries the agent's presence too — it already runs on
// the tick presence wants, and an agent working quietly answers with no new
// messages and a moving status.
export type MessageBatch = AgentPresence & {
  messages: AgentMessageRecord[]
  last_id: number
}

export async function fetchAgentMessages(
  agentsUrl: string,
  agentId: string,
  after: number,
): Promise<MessageBatch | null> {
  try {
    const response = await fetch(`${agentsUrl}/${agentId}/messages?after=${after}`, {
      headers: { "Accept": "application/json" },
    })
    if (!response.ok) return null
    return (await response.json()) as MessageBatch
  } catch {
    return null
  }
}

// Stop one execution an agent has running. Returns null on success and a
// message to show otherwise — a job that is already over comes back as a plain
// sentence, because that is an answer rather than a failure.
export async function stopJob(agentsUrl: string, agentId: string, jobId: string): Promise<string | null> {
  if (!csrfToken()) return "Reload the page and try again."

  try {
    const response = await fetch(`${agentsUrl}/${agentId}/jobs/${encodeURIComponent(jobId)}/stop`, {
      method: "POST",
      headers: jsonHeaders(),
    })
    if (response.ok) return null
    const body = (await response.json().catch(() => null)) as { error?: string } | null
    return body?.error || "Could not stop this job."
  } catch {
    return "Could not reach the server."
  }
}

// Ask a working agent to land what it is doing: it finishes the round without
// the tools that start anything new, and keeps what it found.
export async function wrapUpAgent(agentsUrl: string, agentId: string): Promise<AgentMessageRecord | string> {
  return postForMessage(`${agentsUrl}/${agentId}/wrap_up`, {})
}

// Both return the created transcript row, or a string error to show inline.
export async function startAgent(agentsUrl: string, agentId: string): Promise<AgentMessageRecord | string> {
  return postForMessage(`${agentsUrl}/${agentId}/start`, {})
}

export async function sendAgentMessage(
  agentsUrl: string,
  agentId: string,
  contents: string,
): Promise<AgentMessageRecord | string> {
  return postForMessage(`${agentsUrl}/${agentId}/message`, { message: { contents } })
}

async function postForMessage(url: string, body: unknown): Promise<AgentMessageRecord | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify(body),
    })
    const payload = (await response.json()) as { message?: AgentMessageRecord; error?: string }
    if (!response.ok || !payload.message) return payload.error ?? "Something went wrong."
    return payload.message
  } catch {
    return "Could not reach the server."
  }
}

// Soft delete: the transcript survives, and the workspace is told to stop the
// agent and mark its directory. Returns whether the row was deleted — the
// runtime being unreachable does not undo that.
export async function deleteAgent(agentsUrl: string, agentId: string): Promise<boolean> {
  if (!csrfToken()) return false

  try {
    const response = await fetch(`${agentsUrl}/${agentId}`, {
      method: "DELETE",
      headers: jsonHeaders(),
    })
    return response.ok
  } catch {
    return false
  }
}

export async function updateAgent(
  agentsUrl: string,
  agentId: string,
  fields: { name?: string; model?: string },
): Promise<AgentRecord | null> {
  if (!csrfToken()) return null

  try {
    const response = await fetch(`${agentsUrl}/${agentId}`, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify({ agent: fields }),
    })
    if (!response.ok) return null
    const body = (await response.json()) as { agent: AgentRecord }
    return body.agent
  } catch {
    return null
  }
}
